<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('support.title') }}
          </h1>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-xxl">

        <!-- Historique -->
        <div class="card card-flush">
          <div class="card-header flex-wrap border-0 pt-6 pb-0">
            <div class="card-title">
              <h3 class="card-label">{{ $t('support.history_title') }}</h3>
            </div>
            <div class="card-toolbar">
              <button type="button" class="btn btn-sm btn-primary" @click="openNewTicketModal">
                <i class="bi bi-plus-lg me-1"></i> {{ $t('support.new_ticket_title') }}
              </button>
            </div>
          </div>
          <div class="card-body py-4">
            <div class="table-responsive">
              <table class="table align-middle table-row-bordered fs-6 gy-3" id="kt_datatable_tickets">
                <thead>
                  <tr class="text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0">
                    <th class="min-w-125px">{{ $t('support.col_reference') }}</th>
                    <th class="min-w-125px">{{ $t('support.category_label') }}</th>
                    <th class="min-w-100px">{{ $t('support.priority_label') }}</th>
                    <th class="min-w-100px">{{ $t('support.col_status') }}</th>
                    <th class="min-w-125px">{{ $t('support.col_date') }}</th>
                    <th class="text-end min-w-80px">{{ $t('support.col_actions') }}</th>
                  </tr>
                </thead>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal nouveau ticket -->
    <div class="modal fade" id="newSupportTicketModal" data-bs-backdrop="static" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <form @submit.prevent="submitTicket">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ $t('support.new_ticket_title') }}</h5>
              <button type="button" class="btn-close" @click="closeNewTicketModal"></button>
            </div>
            <div class="modal-body">
              <div class="mb-4">
                <label class="form-label fw-bold">{{ $t('support.category_label') }}</label>
                <select
                  name="category"
                  v-model="form.category"
                  data-control="select2"
                  data-search="true"
                  data-placeholder="Filter"
                  class="form-select kt_select_ticket_category form-select-solid"
                >
                  <option value="assistance">{{ $t('support.category_assistance') }}</option>
                  <option value="incident">{{ $t('support.category_incident') }}</option>
                  <option value="bug">{{ $t('support.category_bug') }}</option>
                  <option value="improvement">{{ $t('support.category_improvement') }}</option>
                  <option value="installation">{{ $t('support.category_installation') }}</option>
                  <option value="configuration">{{ $t('support.category_configuration') }}</option>
                  <option value="billing">{{ $t('support.category_billing') }}</option>
                  <option value="account">{{ $t('support.category_account') }}</option>
                  <option value="security">{{ $t('support.category_security') }}</option>
                  <option value="other">{{ $t('support.category_other') }}</option>
                </select>
              </div>

              <div class="mb-4">
                <label class="form-label fw-bold">{{ $t('support.priority_label') }}</label>
                <div class="d-flex gap-2">
                  <button
                    v-for="level in priorityLevels"
                    :key="level"
                    type="button"
                    class="btn btn-sm flex-fill"
                    :class="form.priority === level ? priorityActiveClass(level) : 'btn-light'"
                    @click="form.priority = level"
                  >{{ $t('support.priority_' + level) }}</button>
                </div>
              </div>

              <div class="mb-4">
                <label class="form-label fw-bold">{{ $t('support.message_label') }}</label>
                <div ref="messageEditor"></div>
                <div
                  class="support-dropzone mt-2"
                  :class="{ 'support-dropzone--active': isDragOver }"
                  @dragover.prevent="isDragOver = true"
                  @dragleave.prevent="isDragOver = false"
                  @drop.prevent="onImageDrop"
                  @click="$refs.imageFileInput.click()"
                >
                  <span v-if="uploadingImage">
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    {{ $t('support.dropzone_uploading') }}
                  </span>
                  <span v-else>
                    <i class="bi bi-image me-2"></i>
                    {{ $t('support.dropzone_label') }}
                  </span>
                  <input ref="imageFileInput" type="file" accept="image/*" class="d-none" @change="onImageFileSelected" />
                </div>

                <div v-if="attachedImages.length" class="support-attachments mt-3">
                  <div v-for="(img, idx) in attachedImages" :key="img.url" class="support-attachment-tile">
                    <img :src="img.url" alt="attachment" />
                    <button type="button" class="support-attachment-remove" @click="removeAttachedImage(idx)">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" @click="closeNewTicketModal" class="btn btn-light">{{ $t('support.cancel') }}</button>
              <button type="submit" class="btn btn-primary" :disabled="isSending || !form.message.trim()">
                <span v-if="isSending" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('support.send') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal fil du ticket -->
    <div class="modal fade" id="viewSupportTicketModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ activeTicket ? activeTicket.reference : '' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingThread" class="text-center py-10">
              <span class="spinner-border spinner-border-sm text-primary"></span>
            </div>
            <div v-else-if="activeTicket">
              <div class="mb-3">
                <div class="text-gray-500 fs-8">{{ formatDate(activeTicket.created_at) }}</div>
                <div class="mb-0 mt-1 ticket-rich-content" v-html="activeTicket.message"></div>
              </div>

              <div
                v-for="msg in threadMessages"
                :key="msg.id"
                class="mb-3 p-3 rounded"
                :style="msg.sender_type === 'admin' ? 'background:#eef8f2;' : 'background:#f5f8fa;'"
              >
                <div class="text-gray-500 fs-8 mb-1">
                  {{ msg.sender_type === 'admin' ? $t('support.support_team') : (msg.user_name || $t('support.you')) }}
                  — {{ formatDate(msg.created_at) }}
                </div>
                <div class="ticket-rich-content" v-html="msg.message"></div>
              </div>

              <div v-if="activeTicket.status !== 'closed'" class="mt-4">
                <textarea v-model="replyDraft" class="form-control form-control-sm" rows="3" :placeholder="$t('support.reply_placeholder')"></textarea>

                <div
                  class="support-dropzone mt-2"
                  :class="{ 'support-dropzone--active': isReplyDragOver }"
                  @dragover.prevent="isReplyDragOver = true"
                  @dragleave.prevent="isReplyDragOver = false"
                  @drop.prevent="onReplyImageDrop"
                  @click="$refs.replyImageFileInput.click()"
                >
                  <span v-if="uploadingReplyImage">
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    {{ $t('support.dropzone_uploading') }}
                  </span>
                  <span v-else>
                    <i class="bi bi-image me-2"></i>
                    {{ $t('support.dropzone_label') }}
                  </span>
                  <input ref="replyImageFileInput" type="file" accept="image/*" class="d-none" @change="onReplyImageFileSelected" />
                </div>

                <div v-if="replyAttachedImages.length" class="support-attachments mt-3">
                  <div v-for="(img, idx) in replyAttachedImages" :key="img.url" class="support-attachment-tile">
                    <img :src="img.url" alt="attachment" />
                    <button type="button" class="support-attachment-remove" @click="replyAttachedImages.splice(idx, 1)">
                      <i class="bi bi-x"></i>
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  class="btn btn-sm btn-primary mt-2"
                  :disabled="sendingReply || !replyDraft.trim()"
                  @click="sendReply"
                >
                  {{ $t('support.reply_send') }}
                </button>
              </div>
              <div v-else class="text-gray-500 fs-7 mt-4">
                {{ $t('support.ticket_closed_notice') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { BASE_URL } from '@/services/api'

export default {
  name: 'MyTicketsView',
  data() {
    return {
      table: null,
      isSending: false,
      newTicketModal: null,
      viewTicketModal: null,
      messageEditor: null,
      isDragOver: false,
      uploadingImage: false,
      attachedImages: [],
      loadingThread: false,
      activeTicket: null,
      threadMessages: [],
      replyDraft: '',
      sendingReply: false,
      replyAttachedImages: [],
      isReplyDragOver: false,
      uploadingReplyImage: false,
      priorityLevels: ['low', 'normal', 'high', 'critical'],
      form: {
        category: 'assistance',
        priority: 'normal',
        message: ''
      }
    }
  },
  computed: {
    getDatatableLocaleUrl() {
      return this.$i18n?.locale === 'fr'
        ? '//cdn.datatables.net/plug-ins/1.10.20/i18n/French.json'
        : '//cdn.datatables.net/plug-ins/1.10.20/i18n/English.json'
    }
  },
  mounted() {
    this.fetchDatatable()
    this.bindRowActions()
    this.initSelect()
    this.initEditor()
    if (this.$route.query.open) {
      this.openViewModal(this.$route.query.open)
    }
  },
  beforeUnmount() {
    this.destroyDataTable()
    if (this.messageEditor) this.messageEditor.destroy()
  },
  methods: {
    async initEditor() {
      if (!window.ClassicEditor || !this.$refs.messageEditor) return
      const editor = await window.ClassicEditor.create(this.$refs.messageEditor, {
        toolbar: [
          'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList',
          '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo'
        ]
      })
      this.messageEditor = markRaw(editor)
      this.messageEditor.model.document.on('change:data', () => {
        this.form.message = this.messageEditor.getData()
      })
    },

    onImageDrop(e) {
      this.isDragOver = false
      const file = e.dataTransfer.files[0]
      if (file) this.uploadAndAttachImage(file)
    },

    onImageFileSelected(e) {
      const file = e.target.files[0]
      if (file) this.uploadAndAttachImage(file)
      e.target.value = ''
    },

    uploadAndAttachImage(file) {
      if (!file.type.startsWith('image/')) return
      this.uploadingImage = true
      const formData = new FormData()
      formData.append('upload', file)
      axios.post('support-tickets/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then((res) => {
        this.attachedImages.push({ url: res.data.url })
      }).catch(() => {
        window.toastr['error'](this.$t('support.image_upload_error'))
      }).finally(() => {
        this.uploadingImage = false
      })
    },

    removeAttachedImage(index) {
      this.attachedImages.splice(index, 1)
    },

    onReplyImageDrop(e) {
      this.isReplyDragOver = false
      const file = e.dataTransfer.files[0]
      if (file) this.uploadAndAttachReplyImage(file)
    },

    onReplyImageFileSelected(e) {
      const file = e.target.files[0]
      if (file) this.uploadAndAttachReplyImage(file)
      e.target.value = ''
    },

    uploadAndAttachReplyImage(file) {
      if (!file.type.startsWith('image/')) return
      this.uploadingReplyImage = true
      const formData = new FormData()
      formData.append('upload', file)
      axios.post('support-tickets/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then((res) => {
        this.replyAttachedImages.push({ url: res.data.url })
      }).catch(() => {
        window.toastr['error'](this.$t('support.image_upload_error'))
      }).finally(() => {
        this.uploadingReplyImage = false
      })
    },

    initSelect() {
      const sel = $('body .kt_select_ticket_category')
      sel.select2()
      sel.change((e) => {
        if (e.target.name === 'category') this.form.category = e.target.value
      })
    },

    destroyDataTable() {
      if ($.fn.dataTable.isDataTable('#kt_datatable_tickets')) {
        $('#kt_datatable_tickets').DataTable().destroy()
      }
    },

    fetchDatatable() {
      const AUTH_TOKEN = Ls.get('auth.token')
      const self = this

      this.destroyDataTable()

      this.table = $('#kt_datatable_tickets').DataTable({
        destroy: true,
        responsive: true,
        processing: true,
        serverSide: false,
        pageLength: Ls.get('pageLength') ?? 10,
        lengthMenu: [5, 10, 25, 50],
        order: [[4, 'desc']],
        ajax: {
          url: BASE_URL + '/support-tickets',
          type: 'GET',
          dataSrc: 'data',
          beforeSend: (request) => {
            request.setRequestHeader('Authorization', 'Bearer ' + AUTH_TOKEN)
          }
        },
        columns: [
          { data: 'reference' },
          { data: 'category' },
          { data: 'priority' },
          { data: 'status' },
          { data: 'created_at' },
          { data: null, responsivePriority: -1 }
        ],
        columnDefs: [
          {
            targets: 1,
            render: (data) => self.$t('support.category_' + data)
          },
          {
            targets: 2,
            render: (data) => '<span class="badge ' + self.priorityBadgeClass(data) + '">' + self.$t('support.priority_' + data) + '</span>'
          },
          {
            targets: 3,
            render: (data) => '<span class="badge ' + self.statusBadgeClass(data) + '">' + self.$t('support.status_' + data) + '</span>'
          },
          {
            targets: 4,
            render: (data) => self.formatDate(data)
          },
          {
            targets: -1,
            className: 'text-end',
            orderable: false,
            render: (data, type, row) => {
              return '<button type="button" data-id="' + row.id + '" class="btn btn-icon btn-active-light-primary w-30px h-30px ticket_view_btn">' +
                '<i class="bi bi-eye ticket_view_btn" data-id="' + row.id + '"></i>' +
                '</button>'
            }
          }
        ],
        language: {
          url: this.getDatatableLocaleUrl
        }
      })
    },

    bindRowActions() {
      $(document).on('click', '#kt_datatable_tickets .ticket_view_btn', (e) => {
        const id = e.target.closest('.ticket_view_btn')?.dataset.id || e.target.dataset.id
        if (id) this.openViewModal(id)
      })
    },

    priorityBadgeClass(priority) {
      return {
        low: 'badge-light-secondary',
        normal: 'badge-light-primary',
        high: 'badge-light-dark',
        critical: 'badge-light-danger'
      }[priority] || 'badge-light-primary'
    },

    statusBadgeClass(status) {
      return {
        new: 'badge-light-primary',
        open: 'badge-light-info',
        closed: 'badge-light-success'
      }[status] || 'badge-light-info'
    },

    priorityActiveClass(priority) {
      return {
        low: 'btn-secondary',
        normal: 'btn-primary',
        high: 'btn-dark',
        critical: 'btn-danger'
      }[priority] || 'btn-primary'
    },

    openNewTicketModal() {
      if (!this.newTicketModal) {
        this.newTicketModal = new bootstrap.Modal(document.getElementById('newSupportTicketModal'))
      }
      this.newTicketModal.show()
    },

    closeNewTicketModal() {
      if (this.newTicketModal) {
        this.newTicketModal.hide()
      }
    },

    submitTicket() {
      if (!this.form.message.trim()) return
      this.isSending = true
      const payload = {
        ...this.form,
        images: this.attachedImages.map(img => img.url)
      }
      axios.post('support-tickets', payload).then(() => {
        window.toastr['success'](this.$t('support.sent_success'))
        this.form.message = ''
        if (this.messageEditor) this.messageEditor.setData('')
        this.form.category = 'assistance'
        this.form.priority = 'normal'
        this.attachedImages = []
        this.closeNewTicketModal()
        this.table.ajax.reload(null, false)
      }).catch(() => {
        window.toastr['error'](this.$t('support.sent_error'))
      }).finally(() => {
        this.isSending = false
      })
    },

    openViewModal(ticketId) {
      if (!this.viewTicketModal) {
        this.viewTicketModal = new bootstrap.Modal(document.getElementById('viewSupportTicketModal'))
      }
      this.viewTicketModal.show()
      this.replyAttachedImages = []
      this.loadThread(ticketId)
    },

    loadThread(ticketId) {
      this.loadingThread = true
      this.replyDraft = ''
      axios.get(`support-tickets/${ticketId}`).then((res) => {
        this.activeTicket = res.data.data
        this.threadMessages = res.data.data.messages || []
      }).finally(() => {
        this.loadingThread = false
      })
    },

    sendReply() {
      if (!this.replyDraft.trim() || !this.activeTicket) return
      this.sendingReply = true
      const payload = {
        message: this.replyDraft,
        images: this.replyAttachedImages.map(img => img.url)
      }
      axios.post(`support-tickets/${this.activeTicket.id}/reply`, payload).then(() => {
        this.replyDraft = ''
        this.replyAttachedImages = []
        window.toastr['success'](this.$t('support.reply_sent'))
        this.table.ajax.reload(null, false)
        this.viewTicketModal.hide()
      }).catch(() => {
        window.toastr['error'](this.$t('support.reply_error'))
      }).finally(() => {
        this.sendingReply = false
      })
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString() + ' ' + new Date(value).toLocaleTimeString().slice(0, 5)
    }
  }
}
</script>

<style scoped>
.ticket-rich-content :deep(p) {
  margin-bottom: 0.5rem;
}
.ticket-rich-content :deep(ul),
.ticket-rich-content :deep(ol) {
  margin-bottom: 0.5rem;
  padding-left: 1.25rem;
}
.ticket-rich-content :deep(blockquote) {
  border-left: 3px solid #e4e6ef;
  padding-left: 0.75rem;
  color: #6c7293;
}

.ticket-rich-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

.ticket-rich-content :deep(.ticket-attachments) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.ticket-rich-content :deep(.ticket-attachments img) {
  width: 100px;
  height: 100px;
  max-width: 100px;
  object-fit: cover;
  border-radius: 6px;
}

.support-dropzone {
  border: 2px dashed #d5d9e0;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  font-size: 0.85rem;
  color: #6c7293;
  cursor: pointer;
  transition: all 0.15s ease;
}

.support-dropzone:hover {
  border-color: var(--kt-primary);
  color: var(--kt-primary);
}

.support-dropzone--active {
  border-color: var(--kt-primary);
  background: var(--kt-primary-light, rgba(0, 158, 247, 0.1));
  color: var(--kt-primary);
}

.support-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.support-attachment-tile {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e4e6ef;
}

.support-attachment-tile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.support-attachment-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  padding: 0;
  line-height: 1;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  cursor: pointer;
}

.support-attachment-remove:hover {
  background: var(--kt-danger, #f1416c);
}
</style>
